import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from '../../../core/services/user.service';
import { FeedbackService } from '../../../core/services/feedback.service';

@Component({
  selector: 'app-lab-order-config-dialog',
  templateUrl: './lab-order-config-dialog.component.html',
  styleUrls: ['./lab-order-config-dialog.component.scss']
})
export class LabOrderConfigDialogComponent implements OnInit {
  configForm!: FormGroup;
  isSaving = false;
  isDoctor: boolean = false;

  targetUser: any = null;
  isUpdatingOtherUser: boolean = false;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private feedbackService: FeedbackService,
    public dialogRef: MatDialogRef<LabOrderConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Check if current user is a doctor
    this.isDoctor = this.userService.isUserPhysician || data.currentUser?.userType === 'doctor';
    this.targetUser = data.targetUser || data.currentUser;
    this.isUpdatingOtherUser = data.isUpdatingOtherUser || false;
    
    // If updating another user, allow non-doctors to set lancetCode for that user
    const canEdit = this.isDoctor || this.isUpdatingOtherUser;
    
    this.configForm = this.fb.group({
      lancetCode: [{ value: this.targetUser?.lancetCode || '', disabled: !canEdit }]
    });
  }

  ngOnInit(): void {
    // Update validators based on what's needed and user type
    const canEdit = this.isDoctor || this.isUpdatingOtherUser;
    
    if (canEdit) {
      // Can set lancetCode (either doctor setting their own, or user setting for selected physician)
      if (this.data.needsLancetCode) {
        this.configForm.get('lancetCode')?.setValidators([Validators.required]);
        this.configForm.get('lancetCode')?.enable();
      }
    } else {
      // Cannot set lancetCode
      this.configForm.get('lancetCode')?.disable();
    }
    this.configForm.updateValueAndValidity();
  }


  async save(): Promise<void> {
    if (this.configForm.invalid) {
      return;
    }

    this.isSaving = true;
    // Use getRawValue() to get values from disabled controls too
    const formValue = this.configForm.getRawValue();
    const updates: any = {};

    try {
      const canEdit = this.isDoctor || this.isUpdatingOtherUser;
      const currentLancetCode = this.targetUser?.lancetCode;
      const newLancetCode = formValue.lancetCode || ''; // Allow empty string to clear

      // Update if value has changed (including clearing it)
      if (canEdit && newLancetCode !== currentLancetCode) {
        if (this.isUpdatingOtherUser && this.targetUser?.userId) {
          // Update another user's lancetCode
          await this.userService.updateUserProfile({ 
            lancetCode: newLancetCode,
            userId: this.targetUser.userId 
          }).toPromise();
          updates.lancetCode = newLancetCode;
          updates.targetUserId = this.targetUser.userId;
        } else {
          // Update current user's lancetCode
          await this.userService.updateUserProfile({ lancetCode: newLancetCode }).toPromise();
          updates.lancetCode = newLancetCode;
        }
      }

      this.feedbackService.showToast('Lab order configuration updated successfully');
      this.dialogRef.close(updates);
    } catch (error: any) {
      this.feedbackService.showToast('Failed to update configuration: ' + (error?.message || 'Unknown error'), 'OK', 8000);
    } finally {
      this.isSaving = false;
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}

